import type {
  ComponentDescription,
  ComponentInstance,
  NodeRedFlow,
  NodeRedFlowEventNode,
  NodeRedFlowTab,
} from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { ComponentTemplate } from '../types';
import { getInitialComponentPropertyBindings, migrateComponentEvents } from '../../../modules/components';
import { getComponentDefaultStyleClass } from '../../styles/utils';
import migrateComponentInstanceLogicFlows from './migrateComponentInstanceLogicFlows';
import generateDeterministicNodeId from '../../../utilities/generateDeterministicNodeId';
import getComponentLogicTabId from '../../logic/getComponentLogicTabId';
import regenerateUniqueComponentInstanceIds from './regenerateUniqueComponentInstanceIds';
import { pickCompositeRootInnerStyles, pickCompositeRootOuterStyles } from '../../../modules/styles';
import omitComponentInstanceOuterLogicFlows from '../../logic/utils/omitComponentInstanceOuterLogicFlows';
import getCompositeTemplateFromDescription from '../../composites/utils/getCompositeTemplateFromDescription';
import addMissingRowComponentCells from './addMissingRowComponentCells';
import { omitUndefinedProperties } from '../../utils/objects';
import migrateComponentInstance from './migrateComponentInstance';

export interface CreateComponentInstanceProperties {
  /**
   * ID of the new instantiated component
   */
  id: string;
  /**
   * Optional display name for the component instance.
   */
  display_name?: string;
  /**
   * Optional bindings for the component instance.
   * If omitted, they will be auto-generated based on the description.
   */
  args?: ComponentInstance['args'];
  /**
   * Optional styles for the component instance.
   */
  style?: ComponentInstance['style'];
  /**
   * ID of the view to which the component instance is to be inserted
   */
  viewId: number;
  /**
   * Optional outer logic for the component instance.
   * If omitted, the default outer logic is generated.
   */
  flows?: NodeRedFlow[];
  /**
   * Optional repeat binding for the component instance
   */
  repeat?: ComponentInstance['repeat'];
  /**
   * Optional style class for the component instance.
   * If omitted, then the default style class of the description is used.
   */
  style_class?: string | null;
  /**
   * Optional nested overrides for composite inner components.
   */
  nested_overrides?: ComponentInstance['nested_overrides'];
  /**
   * Optional conditional renderer child key.
   */
  key?: string;
}

const emptyChildren: never[] = [];
const emptyStyle: Record<string, never> = {};

function getComponentInitialOuterLogic(
  viewId: number,
  componentId: string,
  description: ComponentDescription,
): NodeRedFlow[] {
  const eventsByName = migrateComponentEvents(description.events);
  const logicTabId = getComponentLogicTabId(viewId, componentId);
  const logicTab: NodeRedFlowTab = {
    id: logicTabId,
    type: 'tab',
    label: `Component: ${description.displayName}`,
  };
  const logicFlows: NodeRedFlow[] = [logicTab];
  Object.entries(eventsByName).forEach(([eventName, event], index) => {
    const eventNode: NodeRedFlowEventNode = {
      type: 'event',
      id: generateDeterministicNodeId([viewId, componentId, eventName]),
      idType: 'event',
      eventSource: `${logicTabId}:${eventName}`,
      wires: [[]],
      x: 110,
      y: 40 + index * 60,
      z: logicTabId,
      fallbackName: event?.title || `Component ${eventName}`,
      isConfigurable: true,
    };
    logicFlows.push(eventNode);
  });
  return logicFlows;
}

export default function createComponentInstanceFromDescription(
  description: ComponentDescription,
  properties: CreateComponentInstanceProperties,
): ComponentTemplate {
  const { name, args: argDefinitions } = description;
  const compositeTemplate = getCompositeTemplateFromDescription(description);
  const {
    id,
    args: oldArgs,
    style_class = getComponentDefaultStyleClass(name, description.default_style_classes),
    style = emptyStyle,
    viewId,
    flows: initialFlows,
    ...additionalProps
  } = properties;
  const args = getInitialComponentPropertyBindings(argDefinitions, oldArgs);
  const flows = initialFlows ?? getComponentInitialOuterLogic(viewId, id, description);
  if (compositeTemplate) {
    return createComponentInstanceFromTemplate(compositeTemplate, description, {
      id,
      name,
      args,
      style_class,
      style,
      viewId,
      flows,
      ...additionalProps,
    });
  }
  const newPrimitiveComponentInstance: ComponentInstance = omitUndefinedProperties({
    id,
    args,
    name: description.name,
    display_name: description.displayName,
    children: emptyChildren,
    style,
    style_class,
    ...additionalProps,
  });
  // In case of a Row component, ensure that the children count matches the `columns` property
  return addMissingRowComponentCells(
    viewId,
    newPrimitiveComponentInstance,
    migrateComponentInstanceLogicFlows(flows, viewId, newPrimitiveComponentInstance),
  );
}

interface CreateTemplatedComponentInstanceProperties extends CreateComponentInstanceProperties {
  /**
   * The description identifier of the component
   */
  name: string;
}

function createComponentInstanceFromTemplate(
  template: ComponentTemplate,
  description: ComponentDescription,
  { id, name, style, viewId, flows, ...properties }: CreateTemplatedComponentInstanceProperties,
): ComponentTemplate {
  const uniqueTemplate = regenerateUniqueComponentInstanceIds(
    {
      component: migrateComponentInstance(template.component),
      flows: flows ? omitComponentInstanceOuterLogicFlows(template.flows, id) : template.flows,
    },
    id,
    viewId,
  );
  const templatedComponent = uniqueTemplate.component;
  const newFlows = flows ? flows.concat(uniqueTemplate.flows) : uniqueTemplate.flows;

  const newComponent: ComponentInstance = omitUndefinedProperties({
    // Use the template component as the basis
    ...templatedComponent,
    id,
    name,
    // Use the inner styles from the template, and outer styles as provided as paramteers
    style: {
      ...pickCompositeRootInnerStyles(templatedComponent.style),
      ...pickCompositeRootOuterStyles(style),
    },
    ...properties,
  });
  return {
    component: newComponent,
    flows: migrateComponentInstanceLogicFlows(newFlows, viewId, newComponent, description),
  };
}
